import React, { useState, useEffect } from 'react';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ReportsFilterComponent, ReportsFilterComponentProps } from './ReportsFilterComponent';
import { TimeSheetDetailedReport, UserEntityDetails } from '../../axiosApi/models';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { SCOPES } from '../../common/permissions';
import intl from 'components/i18n/ReactIntlWrapper';
import { getUserEntity } from 'common/UserEntityProvider';
import { handleAPIError } from '../../common/errorHandler';
import { AccessTime, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { Chart, useChart } from 'components/chart';
import { Box, Divider, Skeleton, Stack, Typography, Card, useTheme } from '@mui/material';
import Title from 'components/common/Title';

interface Task {
  idTask: number;
  taskDescription: string;
  timesheetDescription: string;
  time: number;
  idEmployee: number;
  employeeName: string;
  date: Date;
}

interface Job {
  idJob: number;
  jobDescription: string;
  tasks: Task[];
  billableHours: number;
  totalHours: number;
  nonBillableHours: number;
}

interface Client {
  idClient: number;
  clientName: string;
  projects: Project[];
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
}

interface Project {
  idProject: number;
  projectName: string;
  jobs: Job[];
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
}

interface DateEntry {
  date: string;
  billableHours: number;
  nonBillableHours: number;
}

interface Employee {
  idEmployee: number;
  employeeName: string;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  monthlyRate: number;
  hourlyRate: number;
  billingAmount: number;
}

export const options = {
  plugins: {
    title: {
      display: false,
      text: `${intl.get('billingReportComponent.clientRow.tableCel.billable')} vs ${intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}`,
    },
  },
  responsive: true,
  scales: {
    x: {
      stacked: true,

      grid: {
        lineWidth: 0,
      },
    },
    y: {
      stacked: true,
    },
  },
};

type TimesheetReportComponentProps = {
  client?: boolean;
};

export const TimesheetReportComponent = (props: TimesheetReportComponentProps) => {
  const { client } = props;
  const theme = useTheme();
  const api: Api = useApi();
  const toast = useToastMessageQueue();
  const [results, setResults] = React.useState<TimeSheetDetailedReport[]>([]);
  const [clients, setClients] = React.useState<Client[]>([]);
  const [chartData, setChartData] = React.useState<any>(null);
  const [clientChartData, setClientChartData] = React.useState<any>(null);
  const [employeeChartData, setEmployeeChartData] = React.useState<any>(null);
  const [loading, setLoading] = React.useState<Boolean>(true);
  const [errors, setErrors] = useState({});
  const userEntity: UserEntityDetails = getUserEntity();

  const chartLabelStyle = {
    labels: {
      rotate: -45,
      style: {
        fontSize: '10px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  };

  const handleFilterChange = (filterData) => {
    fetchData(filterData);
  };

  useEffect(() => {
    // Actualiza el título del documento usando la API del navegador
    const clientJobTaskTree = createClientProjectJobTaskTree(results);
    setClients(clientJobTaskTree);
  }, [results]);

  const fetchData = async (filterData: ReportsFilterComponentProps) => {
    if (client) {
      fetchClientData(filterData);
    } else {
      fetchUserData(filterData);
    }
  };

  const fetchClientData = async (filterData: ReportsFilterComponentProps) => {
    setLoading(true);
    const response = await api.reportsApi
      .apiVversionTimeSheetDetailedReportClientGet(
        '1',
        filterData.from,
        filterData.to,
        filterData.projects,
        userEntity.entityId,
        filterData.employees,
        filterData.jobTypes,
        {}
      )
      .then((response) => {
        if (response.data.data) {
          setResults(response.data?.data);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchUserData = async (filterData: ReportsFilterComponentProps) => {
    setLoading(true);
    const response = await api.reportsApi
      .apiVversionTimeSheetDetailedReportAllGet(
        '1',
        filterData.from,
        filterData.to,
        filterData.projects,
        filterData.clients,
        filterData.employees,
        filterData.jobTypes,
        {}
      )
      .then((response) => {
        if (response.data.data) {
          setResults(response.data?.data);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getFixed = (value: number) => Number(value).toFixed(2);

  function createClientProjectJobTaskTree(timesheetData: TimeSheetDetailedReport[]): Client[] {
    const clients: Client[] = [];
    const dates: DateEntry[] = [];
    const employees: Employee[] = [];

    for (const entry of timesheetData) {
      let client = clients.find((c) => c.idClient === entry.idClient);

      if (!client) {
        client = {
          idClient: entry.idClient,
          clientName: entry.clientName,
          projects: [],
          billableHours: entry.billable ? entry.time : 0,
          nonBillableHours: entry.billable ? 0 : entry.time,
          totalHours: entry.time,
        };
        clients.push(client);
      } else {
        client.billableHours += entry.billable ? entry.time : 0;
        client.nonBillableHours += entry.billable ? 0 : entry.time;
        client.totalHours += entry.time;
      }

      let project = client.projects.find((p) => p.idProject === entry.idProject);

      if (!project) {
        project = {
          idProject: entry.idProject,
          projectName: entry.projectName,
          jobs: [],
          billableHours: entry.billable ? entry.time : 0,
          nonBillableHours: entry.billable ? 0 : entry.time,
          totalHours: entry.time,
        };
        client.projects.push(project);
      } else {
        project.billableHours += entry.billable ? entry.time : 0;
        project.nonBillableHours += entry.billable ? 0 : entry.time;
        project.totalHours += entry.time;
      }

      let job = project.jobs.find((j) => j.idJob === entry.idJob);

      if (!job) {
        job = {
          idJob: entry.idJob,
          jobDescription: entry.jobDescription,
          tasks: [],
          billableHours: entry.billable ? entry.time : 0,
          nonBillableHours: !entry.billable ? entry.time : 0,
          totalHours: entry.time,
        };
        project.jobs.push(job);
      } else {
        job.billableHours += entry.billable ? entry.time : 0;
        job.nonBillableHours += entry.billable ? 0 : entry.time;
        job.totalHours += entry.time;
      }

      const task: Task = {
        idTask: entry.idTimesheet,
        taskDescription: entry.taskDescription,
        timesheetDescription: entry.timesheetDescription,
        time: entry.time,
        idEmployee: entry.idEmployee,
        employeeName: entry.employeeName,
        date: entry.date,
      };
      job.tasks.push(task);

      let date = dates.find((d) => d.date === new Date(entry.date).toLocaleDateString());

      if (!date) {
        date = {
          date: new Date(entry.date).toLocaleDateString(),
          billableHours: entry.billable ? entry.time : 0,
          nonBillableHours: !entry.billable ? entry.time : 0,
        };
        dates.push(date);
      } else {
        date.billableHours += entry.billable ? entry.time : 0;
        date.nonBillableHours += entry.billable ? 0 : entry.time;
      }

      let employee = employees.find((j) => j.idEmployee === entry.idEmployee);
      if (!employee) {
        employee = {
          idEmployee: entry.idEmployee,
          employeeName: entry.employeeName,
          billableHours: entry.billable ? entry.time : 0,
          nonBillableHours: entry.billable ? 0 : entry.time,
          totalHours: entry.time,
          hourlyRate: entry.hourlyRate,
          monthlyRate: entry.monthlyRate,
          billingAmount: !entry.billable
            ? 0
            : entry.hourlyRate > 0
              ? (entry.time / 60) * entry.hourlyRate
              : 0,
        };
        employees.push(employee);
      } else {
        employee.billableHours += entry.billable ? entry.time : 0;
        employee.nonBillableHours += entry.billable ? 0 : entry.time;
        employee.totalHours += entry.time;
        employee.billingAmount += !entry.billable
          ? 0
          : entry.hourlyRate > 0
            ? (entry.time * entry.hourlyRate) / 60
            : 0;
      }
    }

    //Pie Data
    const chartDataFormatted = {
      categories: dates.map((c) => c.date.toLocaleString()),
      series: [
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.billable')}`,
          data: dates.map((c) => parseFloat(getFixed(c.billableHours / 60)) || null),
        },
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}`,
          data: dates.map((c) => parseFloat(getFixed(c.nonBillableHours / 60)) || null),
        },
      ],
    };

    setChartData(chartDataFormatted);

    const sortedClients = clients.sort((a, b) => {
      if (a.billableHours > b.billableHours) return -1;
      if (a.billableHours < b.billableHours) return 1;
      return 0;
    });

    const clientChartDataFormatted = {
      categories: sortedClients.map((c) => c.clientName),
      series: [
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.billable')}`,
          data: sortedClients.map((c) => parseFloat(getFixed(c.billableHours / 60)) || null),
        },
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}`,
          data: sortedClients.map((c) => parseFloat(getFixed(c.nonBillableHours / 60)) || null),
        },
      ],
    };
    setClientChartData(clientChartDataFormatted);

    const sortedEmployees = employees.sort((a, b) => {
      if (a.totalHours > b.totalHours) return -1;
      if (a.totalHours < b.totalHours) return 1;
      return 0;
    });

    const employeeChartDataFormatted = {
      categories: sortedEmployees.map((c) => c.employeeName),
      series: [
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.billable')}`,
          data: sortedEmployees.map((c) => parseFloat(getFixed(c.billableHours / 60)) || null),
        },
        {
          name: `${intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}`,
          data: sortedEmployees.map((c) => parseFloat(getFixed(c.nonBillableHours / 60)) || null),
        },
      ],
    };
    setEmployeeChartData(employeeChartDataFormatted);
    return clients;
  }

  function JobRow(props: { jobRow: Job }) {
    const { jobRow } = props;
    const [open2, setOpen2] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<'date' | 'employeeName' | 'taskDescription' | 'time'>('date');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('asc');

    const handleSort = (field: typeof sortBy) => {
      const isAsc = sortBy === field && sortOrder === 'asc';
      setSortOrder(isAsc ? 'desc' : 'asc');
      setSortBy(field);
    };

    const sortedTasks = React.useMemo(() => {
      return [...jobRow.tasks].sort((a, b) => {
        let aVal: any = a[sortBy];
        let bVal: any = b[sortBy];
        
        if (sortBy === 'date') {
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
        }
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortOrder === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      });
    }, [jobRow.tasks, sortBy, sortOrder]);

    return (
      <>
        <TableRow>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen2(!open2)}>
              {open2 ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
            {jobRow.jobDescription}
          </TableCell>
          <TableCell align="right">{getFixed(jobRow.totalHours / 60)}</TableCell>
          <TableCell align="right">{getFixed(jobRow.billableHours / 60)}</TableCell>
          <TableCell align="right">{getFixed(jobRow.nonBillableHours / 60)}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell></TableCell>
          <TableCell></TableCell>
          <TableCell colSpan={4} align="right" style={{ paddingRight: 0 }}>
            <Collapse in={open2} timeout="auto" unmountOnExit>
              <Table key={jobRow.idJob} size="small" padding="normal">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'date'}
                        direction={sortBy === 'date' ? sortOrder : 'asc'}
                        onClick={() => handleSort('date')}
                      >
                        <b>{intl.get('timesheetreport.date')}</b>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'employeeName'}
                        direction={sortBy === 'employeeName' ? sortOrder : 'asc'}
                        onClick={() => handleSort('employeeName')}
                      >
                        <b>{intl.get('timesheetreport.employee')}</b>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'taskDescription'}
                        direction={sortBy === 'taskDescription' ? sortOrder : 'asc'}
                        onClick={() => handleSort('taskDescription')}
                      >
                        <b>{intl.get('timesheetreport.taskdescription')}</b>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === 'time'}
                        direction={sortBy === 'time' ? sortOrder : 'asc'}
                        onClick={() => handleSort('time')}
                      >
                        <b>{intl.get('timesheetreport.hours')}</b>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTasks.map((task) => (
                    <TaskRow key={`${task.idTask}-task`} task={task}></TaskRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  }

  function ProjectRow(props: { row: Project }) {
    const { row } = props;
    const [open, setOpen] = React.useState(false);
    const [sortBy, setSortBy] = React.useState<'jobDescription' | 'totalHours' | 'billableHours' | 'nonBillableHours'>('totalHours');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const handleSort = (field: typeof sortBy) => {
      const isAsc = sortBy === field && sortOrder === 'asc';
      setSortOrder(isAsc ? 'desc' : 'asc');
      setSortBy(field);
    };

    const sortedJobs = React.useMemo(() => {
      return [...row.jobs].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortOrder === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      });
    }, [row.jobs, sortBy, sortOrder]);

    return (
      <React.Fragment>
        <TableRow>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
          <TableCell scope="row">
            <b>{row.projectName}</b>
          </TableCell>
          <TableCell></TableCell>
          <TableCell scope="row" align="right">
            {getFixed(row.totalHours / 60)}
          </TableCell>
          <TableCell scope="row" align="right">
            {getFixed(row.billableHours / 60)}
          </TableCell>
          <TableCell scope="row" align="right">
            {getFixed(row.nonBillableHours / 60)}
          </TableCell>
        </TableRow>
        {open && sortedJobs.map((jobRow) => <JobRow key={jobRow.idJob} jobRow={jobRow}></JobRow>)}
      </React.Fragment>
    );
  }

  function ClientRow(props: { client: Client }) {
    const { client } = props;
    const [sortBy, setSortBy] = React.useState<'projectName' | 'totalHours' | 'billableHours' | 'nonBillableHours'>('totalHours');
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');

    const handleSort = (field: typeof sortBy) => {
      const isAsc = sortBy === field && sortOrder === 'asc';
      setSortOrder(isAsc ? 'desc' : 'asc');
      setSortBy(field);
    };

    const sortedProjects = React.useMemo(() => {
      return [...client.projects].sort((a, b) => {
        let aVal = a[sortBy];
        let bVal = b[sortBy];
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortOrder === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }
        
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        return sortOrder === 'asc' ? aNum - bNum : bNum - aNum;
      });
    }, [client.projects, sortBy, sortOrder]);

    return (
      <Stack gap={2}>
        <Typography variant="h4" color="primary">
          {client.clientName}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'projectName'}
                    direction={sortBy === 'projectName' ? sortOrder : 'asc'}
                    onClick={() => handleSort('projectName')}
                  >
                    <b>{intl.get('billingReportComponent.clientRow.tableCel.project')}</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <b>{intl.get('billingReportComponent.clientRow.tableCel.job')}</b>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'totalHours'}
                    direction={sortBy === 'totalHours' ? sortOrder : 'asc'}
                    onClick={() => handleSort('totalHours')}
                  >
                    <b>{intl.get('billingReportComponent.clientRow.tableCel.totalHours')}</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'billableHours'}
                    direction={sortBy === 'billableHours' ? sortOrder : 'asc'}
                    onClick={() => handleSort('billableHours')}
                  >
                    <b>{intl.get('billingReportComponent.clientRow.tableCel.billable')}</b>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortBy === 'nonBillableHours'}
                    direction={sortBy === 'nonBillableHours' ? sortOrder : 'asc'}
                    onClick={() => handleSort('nonBillableHours')}
                  >
                    <b>{intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}</b>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProjects.map((row) => (
                <ProjectRow key={row.idProject} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  }

  function TaskRow(props: { task: Task }) {
    const { task } = props;
    return (
      <TableRow key={task.idTask}>
        <TableCell>{new Date(task.date).toLocaleDateString()} </TableCell>
        <TableCell component="th" scope="row">
          {task.employeeName}
        </TableCell>
        <TableCell>
          {task.taskDescription}
          {!!task.timesheetDescription && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
              {task.timesheetDescription}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">{getFixed(task.time / 60)}</TableCell>
      </TableRow>
    );
  }

  // Configure chart options using the useChart hook
  const chartOptions = useChart({
    colors: [theme.palette.info.main, theme.palette.error.main],
    chart: {
      stacked: true,
    },
    stroke: {
      width: 0,
    },
    plotOptions: {
      bar: {
        columnWidth: '40%',
        borderRadius: 6,
      },
    },
    xaxis: {
      tickPlacement: 'on',
    },
    tooltip: {
      y: {
        formatter: (value: number) => `${value} hours`,
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
    },
  });

  console.log('Chart options colors:', chartOptions.colors);

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Title>{intl.get('reportsComponent.header')}</Title>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        <AccessTime /> {intl.get('reportsComponent.header')}
      </Typography>
      <Box>
        <ReportsFilterComponent
          handleFilterChange={handleFilterChange}
          errors={errors}
          setErrors={setErrors}
        ></ReportsFilterComponent>
        
        {/* Total Hours Summary Card */}
        {clients.length > 0 && !loading && (
          <Card variant="outlined" sx={{ mb: 3, mt: 2, bgcolor: 'info.lighter' }}>
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {intl.get('timesheetReportComponent.total.title')}
                </Typography>
                <Typography variant="h3" color="info.main" fontWeight="bold">
                  {(clients.reduce((sum, client) => sum + client.totalHours, 0) / 60).toFixed(2)} {intl.get('timesheetReportComponent.total.hours')}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  {intl.get('billingReportComponent.clientRow.tableCel.billable')}: {(clients.reduce((sum, client) => sum + client.billableHours, 0) / 60).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.get('billingReportComponent.clientRow.tableCel.nonBillable')}: {(clients.reduce((sum, client) => sum + client.nonBillableHours, 0) / 60).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {intl.get('timesheetReportComponent.total.clients')}: {clients.length}
                </Typography>
              </Box>
            </Box>
          </Card>
        )}
        
        <Box display={'flex'} alignContent={'center'} justifyContent={'center'} mt={2}>
          <Box maxWidth={'md'} sx={{ width: '100%' }}>
            <Typography variant="h4">{intl.get('reportsComponent.body.date.header')}</Typography>
            {loading && <Skeleton variant="rounded" width={'100%'} height={350} />}
            {chartData && !loading && (
              <Chart
                type="bar"
                series={chartData.series}
                options={{
                  ...chartOptions,
                  colors: [theme.palette.info.main, theme.palette.error.main],
                  xaxis: {
                    ...chartLabelStyle,
                    categories: chartData.categories,
                  },
                }}
                height={350}
              />
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 6 }} />
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box
            sx={{ width: '100%' }}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
          >
            <Box maxWidth={'sm'} sx={{ width: '100%' }}>
              <Typography variant="h4">
                {intl.get('reportsComponent.body.member.header')}
              </Typography>
              {loading && <Skeleton variant="rounded" width={'100%'} height={350} />}
              {employeeChartData && !loading && (
                <Chart
                  type="bar"
                  series={employeeChartData.series}
                  options={{
                    ...chartOptions,
                    colors: [theme.palette.info.main, theme.palette.error.main],
                    xaxis: { ...chartLabelStyle, categories: employeeChartData.categories },
                  }}
                  height={350}
                />
              )}
            </Box>
          </Box>
          {!client && (
            <Box
              sx={{ width: '100%' }}
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
            >
              <Box maxWidth={'sm'} sx={{ width: '100%' }}>
                <Typography variant="h4">
                  {intl.get('reportsComponent.body.client.header')}
                </Typography>
                {loading && <Skeleton variant="rounded" width={'100%'} height={350} />}
                {clientChartData && !loading && (
                  <Chart
                    type="bar"
                    series={clientChartData.series}
                    options={{
                      ...chartOptions,
                      colors: [theme.palette.info.main, theme.palette.error.main],
                      xaxis: { categories: clientChartData.categories },
                    }}
                    height={350}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
        <Stack gap={4}>
          <PermissionsGate
            viewScopes={[SCOPES['reports.timesheet.read']]}
            editScopes={[SCOPES['reports.timesheet.read']]}
            viewRoles={[]}
            editRoles={[]}
          >
            {loading && (
              <Box sx={{ width: '100%' }}>
                <Skeleton variant="text" width={'100%'} height={350} />
              </Box>
            )}
            {!loading &&
              clients &&
              clients.map((client, i) => (
                <ClientRow key={client.idClient} client={client}></ClientRow>
              ))}
          </PermissionsGate>
        </Stack>
      </Box>
    </Box>
    </Card>
  );
};

export default TimesheetReportComponent;
